import { getActiveBackend } from '@/domains/auth/authBackend';
import { getSelectedPerspective } from '@/domains/auth/selectedPerspective';
import { getSelectedTenant } from '@/domains/auth/selectedTenant';

import { apiError } from './apiError';

export { ApiError } from './apiError';

const API_BASE = import.meta.env.VITE_API_URL ?? '';
const APPLICATION_ID = import.meta.env.VITE_APPLICATION_ID ?? '';
const USE_MOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

let tokenPromise: Promise<string> | null = null;
let tokenExpiresAt = 0;

function parseTokenExpiry(token: string): number {
  try {
    const parts = token.split('.');
    if (!parts[1]) return 0;
    const payload = JSON.parse(atob(parts[1])) as { exp?: number };
    // Refresh 60s before actual expiry
    return (payload.exp ?? 0) * 1000 - 60_000;
  } catch {
    return 0;
  }
}

async function fetchDevToken(): Promise<string> {
  const email = getActiveBackend()?.getCurrentEmail() ?? null;
  const response = await fetch(`${API_BASE}/dev/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(email !== null ? { email } : {}),
  });

  if (!response.ok) {
    throw new Error(`Failed to get dev token: ${response.status}`);
  }

  const data = (await response.json()) as { token: string };
  tokenExpiresAt = parseTokenExpiry(data.token);
  return data.token;
}

export function resetApiToken(): void {
  tokenPromise = null;
  tokenExpiresAt = 0;
}

async function getToken(forceRefresh = false): Promise<string> {
  if (!USE_MOCK_AUTH) {
    // Echt pad: MSAL levert (en cachet) de token voor de tenant.
    const backend = getActiveBackend();
    if (!backend) throw new Error('Geen tenant geselecteerd — log opnieuw in.');
    return backend.getAccessToken(forceRefresh);
  }
  if (forceRefresh || !tokenPromise || Date.now() >= tokenExpiresAt) {
    tokenPromise = fetchDevToken();
  }
  return tokenPromise;
}

function buildHeaders(token: string, extra?: HeadersInit): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'X-Application-Id': APPLICATION_ID,
  };
  const tenantCode = getSelectedTenant();
  if (tenantCode) headers['x-tenant-id'] = tenantCode;
  const perspectiveId = getSelectedPerspective();
  if (perspectiveId) headers['x-perspective-id'] = perspectiveId;
  return { ...headers, ...(extra as Record<string, string> | undefined) };
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const token = await getToken();

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...buildHeaders(token, init?.headers),
    },
  });

  // Token geweigerd — forceer een vers token en probeer één keer opnieuw.
  if (response.status === 401) {
    const freshToken = await getToken(true);
    const retry = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...buildHeaders(freshToken, init?.headers),
      },
    });

    if (!retry.ok) {
      throw apiError(retry.status, retry.statusText);
    }

    return retry.json() as Promise<T>;
  }

  if (!response.ok) {
    throw apiError(response.status, response.statusText);
  }

  return response.json() as Promise<T>;
}

export async function apiFetchBlob(
  path: string,
  init?: RequestInit,
): Promise<Blob> {
  const token = await getToken();

  let response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: buildHeaders(token, init?.headers),
  });

  if (response.status === 401) {
    const freshToken = await getToken(true);
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: buildHeaders(freshToken, init?.headers),
    });
  }

  if (!response.ok) {
    throw apiError(response.status, response.statusText);
  }

  return response.blob();
}
